import {
  ACCOUNTS_QUERY,
  ACCOUNT_MUTATION,
  CREATE_ACCOUNT_MUTATION,
  DELETE_ACCOUNT_MUTATION,
  TAX_CODES_QUERY,
} from "@/apis/gql/apolloClient";
import type {
  AccountDetails,
  IInputAccountUpdate,
  TaxCode,
} from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { useMutation, useQuery } from "@apollo/client";
import React, { createContext, useEffect, useState } from "react";

interface AccountsDateProviderProps {
  children: React.ReactNode; // This type is appropriate for children
}

interface AccountsContextProps {
  loading: boolean;
  accounts: AccountDetails[];
  taxCodes: TaxCode[];
  refetch: () => Promise<any>;
  updateAccount: ({
    variables,
  }: {
    variables: IInputAccountUpdate;
  }) => Promise<any>;
  deleteAccount: (accountId: string) => Promise<any>;
  createAccount: (variables: Partial<AccountDetails>) => Promise<any>;
}

const AccountsContext = createContext<AccountsContextProps | undefined>(
  undefined,
);

export const AccountsDataProvider: React.FC<AccountsDateProviderProps> = ({
  children,
}) => {
  const { tenantId } = useTenant();

  const { data, error, loading, refetch } = useQuery(ACCOUNTS_QUERY, {
    variables: { tenantId },
  });
  const {
    data: taxCodesData,
    error: taxCodesError,
    loading: taxCodesLoading,
    refetch: refetchTaxCodfes,
  } = useQuery(TAX_CODES_QUERY, {
    variables: { tenantId },
  });
  const [accounts, setAccounts] = useState<AccountDetails[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [updateAccount] = useMutation(ACCOUNT_MUTATION);
  const [deleteAccount] = useMutation(DELETE_ACCOUNT_MUTATION);
  const [createAccount] = useMutation(CREATE_ACCOUNT_MUTATION);

  const handleAccountDelete = async (accountId: string) => {
    const a = accounts.filter((account) => account.accountId !== accountId);
    setAccounts([...a]);
    await deleteAccount({ variables: { accountId } });
  };

  useEffect(() => {
    if (data) {
      setAccounts(data.accounts);
    }
    if (taxCodesData) {
      setTaxCodes(taxCodesData.taxCodes);
    }
  }, [data, taxCodesData]);

  const handleUpdateAccount = async ({
    variables,
  }: {
    variables: IInputAccountUpdate;
  }) => {
    const updatedAccountIndex = accounts.findIndex(
      (account) => account.accountId === variables.accountId,
    );

    if (updatedAccountIndex !== -1) {
      const updatedAccounts = [...accounts];

      updatedAccounts[updatedAccountIndex] = {
        ...updatedAccounts[updatedAccountIndex],
        ...variables.updatedFields,
      };

      setAccounts(structuredClone(updatedAccounts));
    }

    await updateAccount({ variables });
  };

  const handleCreateAccount = async (variables: Partial<AccountDetails>) => {
    const res = await createAccount({ variables });
    await refetch();
    return res;
  };

  return (
    <AccountsContext.Provider
      value={{
        accounts: accounts ?? [],
        taxCodes: taxCodes ?? [],
        loading,
        refetch,
        updateAccount: handleUpdateAccount,
        deleteAccount: handleAccountDelete,
        createAccount: handleCreateAccount,
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
};

export function useAccountsDataContext() {
  const context = React.useContext(AccountsContext);
  if (context === undefined) {
    throw new Error(
      "useAccountsContext must be used within a AccountsDataProvider",
    );
  }
  return context;
}
